import type {
  CallsIncidentResponsePlan,
  CallsResponseAuthority,
} from './CallsIncidentResponsePlanningEngine';

export type CallsRepairAuthorityDecision =
  | 'approved'
  | 'denied'
  | 'withheld';

export type CallsRepairAuthorityReason =
  | 'authority-confirmed'
  | 'missing-authority-grant'
  | 'authority-mismatch'
  | 'plan-not-active'
  | 'verification-not-required'
  | 'invalid-plan';

export type CallsRepairAuthorityGrant = Readonly<{
  planId: string;
  incidentId: string;
  authority: CallsResponseAuthority;
  granted: boolean;
  grantedAt: string;
  evidenceId: string;
}>;

export type CallsRepairAuthorityResult = Readonly<{
  planId: string;
  incidentId: string;
  incidentKey: string;
  requiredAuthority: CallsResponseAuthority;
  decision: CallsRepairAuthorityDecision;
  reason: CallsRepairAuthorityReason;
  executionAllowed: boolean;
  authorityEvidenceId: string | null;
  evaluatedAt: string;
  verificationRequired: boolean;
}>;

type UnknownRecord = Record<string, unknown>;

const asRecord = (
  value: unknown,
): UnknownRecord =>
  value !== null && typeof value === 'object'
    ? (value as UnknownRecord)
    : {};

const asString = (
  value: unknown,
  fallback: string,
): string =>
  typeof value === 'string' &&
  value.trim().length > 0
    ? value
    : fallback;

const asBoolean = (
  value: unknown,
): boolean => value === true;

const validPlanStatus = (
  value: unknown,
): boolean =>
  value === 'planned' ||
  value === 'awaiting-authority' ||
  value === 'awaiting-verification';

const validatePlan = (
  value: unknown,
): value is CallsIncidentResponsePlan => {
  const plan = asRecord(value);

  return (
    typeof plan.id === 'string' &&
    plan.id.length > 0 &&
    typeof plan.incidentId === 'string' &&
    plan.incidentId.length > 0 &&
    typeof plan.incidentKey === 'string' &&
    plan.incidentKey.length > 0 &&
    typeof plan.requiredAuthority === 'string' &&
    validPlanStatus(plan.status)
  );
};

const findAuthorityGrant = (
  plan: CallsIncidentResponsePlan,
  values: readonly unknown[],
): CallsRepairAuthorityGrant | null => {
  for (const value of values) {
    const grant = asRecord(value);

    if (
      grant.planId === plan.id &&
      grant.incidentId === plan.incidentId &&
      grant.authority ===
        plan.requiredAuthority &&
      asBoolean(grant.granted) &&
      typeof grant.evidenceId === 'string' &&
      grant.evidenceId.length > 0 &&
      typeof grant.grantedAt === 'string' &&
      grant.grantedAt.length > 0
    ) {
      return Object.freeze({
        planId: grant.planId,
        incidentId: grant.incidentId,
        authority:
          grant.authority as CallsResponseAuthority,
        granted: true,
        grantedAt: grant.grantedAt,
        evidenceId: grant.evidenceId,
      });
    }
  }

  return null;
};

const hasMismatchedGrant = (
  plan: CallsIncidentResponsePlan,
  values: readonly unknown[],
): boolean =>
  values.some(value => {
    const grant = asRecord(value);

    return (
      grant.planId === plan.id &&
      grant.incidentId === plan.incidentId &&
      asBoolean(grant.granted) &&
      grant.authority !==
        plan.requiredAuthority
    );
  });

export function evaluateCallsRepairAuthority(
  planValue: unknown,
  grants: readonly unknown[],
  evaluatedAtValue: unknown,
): CallsRepairAuthorityResult {
  const evaluatedAt = asString(
    evaluatedAtValue,
    'Unknown',
  );

  if (!validatePlan(planValue)) {
    return Object.freeze({
      planId: 'Unknown',
      incidentId: 'Unknown',
      incidentKey: 'Unknown',
      requiredAuthority: 'Repair Governor',
      decision: 'denied',
      reason: 'invalid-plan',
      executionAllowed: false,
      authorityEvidenceId: null,
      evaluatedAt,
      verificationRequired: true,
    });
  }

  const plan = planValue;

  if (plan.verificationRequired !== true) {
    return Object.freeze({
      planId: plan.id,
      incidentId: plan.incidentId,
      incidentKey: plan.incidentKey,
      requiredAuthority:
        plan.requiredAuthority,
      decision: 'denied',
      reason: 'verification-not-required',
      executionAllowed: false,
      authorityEvidenceId: null,
      evaluatedAt,
      verificationRequired: true,
    });
  }

  if (
    plan.status !== 'planned' &&
    plan.status !== 'awaiting-authority'
  ) {
    return Object.freeze({
      planId: plan.id,
      incidentId: plan.incidentId,
      incidentKey: plan.incidentKey,
      requiredAuthority:
        plan.requiredAuthority,
      decision: 'withheld',
      reason: 'plan-not-active',
      executionAllowed: false,
      authorityEvidenceId: null,
      evaluatedAt,
      verificationRequired: true,
    });
  }

  const authorityGrant =
    findAuthorityGrant(plan, grants);

  if (!authorityGrant) {
    const mismatch = hasMismatchedGrant(
      plan,
      grants,
    );

    return Object.freeze({
      planId: plan.id,
      incidentId: plan.incidentId,
      incidentKey: plan.incidentKey,
      requiredAuthority:
        plan.requiredAuthority,
      decision: mismatch
        ? 'denied'
        : 'withheld',
      reason: mismatch
        ? 'authority-mismatch'
        : 'missing-authority-grant',
      executionAllowed: false,
      authorityEvidenceId: null,
      evaluatedAt,
      verificationRequired: true,
    });
  }

  return Object.freeze({
    planId: plan.id,
    incidentId: plan.incidentId,
    incidentKey: plan.incidentKey,
    requiredAuthority:
      plan.requiredAuthority,
    decision: 'approved',
    reason: 'authority-confirmed',
    executionAllowed: true,
    authorityEvidenceId:
      authorityGrant.evidenceId,
    evaluatedAt,
    verificationRequired: true,
  });
}

export function evaluateCallsRepairAuthorities(
  plans: readonly unknown[],
  grants: readonly unknown[],
  evaluatedAt: unknown,
): readonly CallsRepairAuthorityResult[] {
  return Object.freeze(
    plans.map(plan =>
      evaluateCallsRepairAuthority(
        plan,
        grants,
        evaluatedAt,
      ),
    ),
  );
}
